"use client";
import { Checkbox, Divider, List, Skeleton } from "antd";
import useMobileDetect from "@/hooks/useMobileDetect";
import { BravveInputNumber } from "../common/BravveInputNumber";
import Global from "@/utils/global";
import { useEffect, useState } from "react";
import { CartListProps, saleAttributeProps } from "./CartList";
import { changeUrl, handleImgUrl } from "@/utils/tools";
import { useRouter } from "next/navigation";
import Eco from "@/app/assets/eco/EcoLeaf.svg";
import ImageWithSkeleton from "../common/ImageWithSkeleton";

export interface clacPriceProps {
  /**原价总和 */
  itemSubTotal: number;
  /**减免金额 */
  itemDiscount: number;
  /**售价总和 */
  estimatedTotal: number;
}

export type CartItemProps = {
  /** 类型 购物车 | 购物车抽屉 | 结算页面 */
  shopCartType: "shoppingCart" | "drawer" | "checkout";
  /** 购物车商品原始列表数据 */
  cartData: CartListProps[];
  /** 计算购物车商品数据接口 */
  apiCalculateShopingCart?: ({}: {
    cartIdList: string[];
  }) => Promise<any> | undefined;
  /** 切换商品选中状态 */
  apiToggleShopingCartSelected?: ({}: {
    idList: string[];
    selected: boolean;
  }) => Promise<any> | undefined;
  /** 操作购物车商品数量 */
  apiShopingCartNum?: ({}: {
    cartId: string;
    count: number;
    operate: string;
  }) => Promise<any> | undefined;
  /** 删除购物车商品接口 */
  apiDeleteShopingCart?: ({}: {
    cartIdList: string[];
  }) => Promise<any> | undefined;
  /** 更新购物车回调 */
  updateCartChange?: () => void;
  /** 计算价格回调 */
  clacPriceChange?: (clacPriceObj: clacPriceProps) => void;
  /** 选择的商品回调 */
  selectItemChange?: (data: string[]) => void;
  /** 删除购物车回调 */
  deleteCartChange?: () => void;
};

export const CartItem: React.FC<CartItemProps> = ({ ...props }) => {
  const isMobile = useMobileDetect();
  const {
    shopCartType,
    cartData,
    apiCalculateShopingCart,
    apiToggleShopingCartSelected,
    apiShopingCartNum,
    apiDeleteShopingCart,
    clacPriceChange,
    selectItemChange,
    updateCartChange,
    deleteCartChange,
  } = props;

  const router = useRouter();
  // 购物车数据
  const [cartItems, setCartItems] = useState(cartData || []);
  // 选中的购物车数据
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  // 全选中间状态
  const indeterminate =
    selectedItems.length > 0 && selectedItems.length < cartItems.length;
  // 是否是抽屉
  const isDrawer = shopCartType === "drawer";

  useEffect(() => {
    setCartItems(cartData);
  }, [cartData]);

  useEffect(() => {
    onChangeItemCount(cartItems);
  }, [cartItems]);

  /**
   * 收集选中的商品
   */
  const onChangeItemCount = async (cartItems: CartListProps[]) => {
    // 收集 item.selected 为true的id
    const selectedIds: string[] = [];
    cartItems.forEach((item: any) => {
      if (item.selected) {
        selectedIds.push(item.id);
      }
    });
    setSelectedItems(selectedIds);
    calcTotalPrice(selectedIds);
    selectItemChange && selectItemChange(selectedIds);
  };

  /**
   * 购物车修改商品数量操作接口
   */
  const calculateItem = async (id: string, type: string) => {
    if (!apiShopingCartNum) return;

    const { status } = await apiShopingCartNum({
      cartId: id,
      operate: type,
      count: 1,
    });
    if (status === 200) {
      updateCartChange && updateCartChange();
    }
  };

  /**
   * 删除购物车商品
   */
  const deleteItem = async (id: string, idx: number) => {
    if (!apiDeleteShopingCart) return;

    const { status } = await apiDeleteShopingCart({ cartIdList: [id] });
    if (status === 200) {
      deleteCartChange && deleteCartChange();
      // 从列表中删除
      // setCartItems(cartItems.filter((item: any, i: number) => i !== index))
    }

    // @ts-ignore
    if (globalThis.gtag) {
      // 触发 view_item 事件
      const userInfoStr = globalThis?.localStorage?.getItem("userInfo");
      const userInfo = userInfoStr ? JSON.parse(userInfoStr) : {};
      const currentSku = cartItems[idx];
      // @ts-ignore
      globalThis.gtag("event", "remove_from_cart", {
        user_id: userInfo?.id,
        items: JSON.stringify([
          {
            id: currentSku.id,
            name: currentSku.itemName,
            category: currentSku.itemPic,
            price: currentSku.price,
            quantity: currentSku.itemCount,
          },
        ]),
        ...{
          id: currentSku.id,
          name: currentSku.itemName,
          category: currentSku.itemPic,
          price: currentSku.price,
          quantity: currentSku.itemCount,
        },
      });
    }
  };

  /**
   * 计算购物车商品价格
   * @param id
   */
  const calcTotalPrice = async (seleCartItems: string[]) => {
    if (!apiCalculateShopingCart) return;
    if (!seleCartItems || !seleCartItems.length) {
      clacPriceChange &&
        clacPriceChange({
          itemSubTotal: 0,
          itemDiscount: 0,
          estimatedTotal: 0,
        });
      return;
    }
    const cartIdList = seleCartItems;
    const { status, data } = await apiCalculateShopingCart({ cartIdList });
    if (status == 200) {
      clacPriceChange &&
        clacPriceChange({
          itemSubTotal: data?.itemSubTotal,
          itemDiscount: data.itemDiscount,
          estimatedTotal: data.estimatedTotal,
        });
    }
  };

  /**
   * 切换勾选状态
   * @param ids
   * @param type true 选中，false 取消
   */
  const onSelectItemChange = async (ids: string[], type: boolean) => {
    if (!ids || ids.length == 0) {
      return;
    }
    if (!apiToggleShopingCartSelected) return;

    const { data, status } = await apiToggleShopingCartSelected({
      idList: ids || [],
      selected: type,
    });

    return status === 200;
  };

  // 处理选择/取消选择
  const onSelectItem = async (id: string) => {
    const result = await onSelectItemChange([id], !selectedItems.includes(id));
    if (!result) return;

    updateCartChange && updateCartChange();

    // setSelectedItems((prevSelectedItems: any) =>
    //     prevSelectedItems.includes(id)
    //         ? prevSelectedItems.filter((item: string) => item !== id)  // 如果已经选择，取消选择
    //         : [...prevSelectedItems, id] // 如果未选择，加入选中的列表
    // );
  };

  // 处理全选
  const onSelectAll = async () => {
    // 判断是否已经全选
    const isAllSelected =
      selectedItems.length != 0 && selectedItems.length <= cartItems.length;

    let selectedItemIds: string[] = [];

    selectedItemIds = cartItems
      .filter((item: any) => !item.cartProductStatusStr)
      .map((item: any) => item.id); // 提取符合条件的 item.id

    // 调用接口，更新状态
    const result = await onSelectItemChange(selectedItemIds, !isAllSelected);
    if (!result) return;

    updateCartChange && updateCartChange();
    // 如果是全选则清空，否则设置选中的ID
    // setSelectedItems(isAllSelected ? [] : selectedItemIds);
  };

  // 处理规格
  const renderAttr = (attr: saleAttributeProps, disabled: boolean) => {
    if (!attr) return null;
    return isMobile || isDrawer ? (
      <div className="mt-[4px]">
        {Object.entries(attr).map(([key, value], index) => (
          <span
            key={key}
            className={`${disabled ? "text-bravve-sub-200" : "text-bravve-100"}`}
          >
            {value}
            {index == Object.entries(attr).length - 1 ? "" : "，"}
          </span>
        ))}
      </div>
    ) : (
      <>
        {Object.entries(attr).map(([key, value]) => (
          <div key={key} className="text-bravve-sub-100">
            {key}:{" "}
            <span
              className={`${disabled ? "text-bravve-sub-200" : "text-bravve-100"} font-medium`}
            >
              {value}
            </span>
          </div>
        ))}
      </>
    );
  };

  /**
   * 跳转商品详情
   */
  const goToProductDetail = (item: CartListProps) => {
    if (item?.skuUrl) {
      router.push(changeUrl(item.skuUrl));
    }
  };

  // 渲染列表项
  const renderItem = (item: CartListProps, index: number) => {
    const itemDisable = item?.cartProductStatusStr ? true : false;
    return (
      <>
        <div
          className={`flex ${isMobile ? "items-start" : "items-center"}`}
          key={item?.id}
        >
          {shopCartType === "checkout" ? (
            false
          ) : (
            <Checkbox
              style={{ marginTop: isMobile ? "24px" : "0" }}
              disabled={itemDisable}
              className={`w-20`}
              checked={selectedItems.includes(item.id) || false}
              onChange={() => onSelectItem(item.id)}
            />
          )}
          <div
            className={`${shopCartType === "checkout" ? "" : "ml-18"} flex-1`}
          >
            <div
              className={`flex ${isMobile || isDrawer ? "items-start" : "items-center"}`}
            >
              <div
                className={`${isMobile ? "h-74 w-74" : isDrawer ? "h-[124px] w-[124px]" : "h-200 w-200"} relative flex items-center justify-center rounded-[2px] bg-bravve-bg-100`}
              >
                <ImageWithSkeleton
                  src={handleImgUrl(Global.imageUrl! + item.itemPic, "200x200")}
                  width={isMobile ? 74 : isDrawer ? 124 : 200}
                  height={isMobile ? 74 : isDrawer ? 124 : 200}
                  alt={item?.itemName || item?.productName || ""}
                  isButton={false}
                />
                {!isMobile && false ? (
                  <div className="absolute left-6 top-6">
                    <Eco width={26} alt="Eco" />
                  </div>
                ) : null}
              </div>
              <div
                className={`${isMobile || isDrawer ? "" : "h-200"} ml-20 flex flex-1 flex-col justify-between`}
              >
                <div className="mb-18">
                  <div
                    onClick={() => {
                      goToProductDetail(item);
                    }}
                    className={`${itemDisable ? "text-bravve-sub-200" : "text-bravve-100"} ${isDrawer || isMobile ? "" : "mb-[8px] font-medium"} line-clamp-2 cursor-pointer text-[1.6rem] hover:underline`}
                  >
                    {item?.itemName || item?.productName}
                  </div>
                  {renderAttr(
                    item.saleAttribute || item?.itemAttribute,
                    itemDisable,
                  )}
                  {isMobile ? (
                    <div className="mt-4">
                      <span
                        className={`${itemDisable ? "text-bravve-sub-200" : "text-bravve-100"} text-[16px] font-medium leading-[24px]`}
                      >
                        ${item?.price || item?.payPrice}
                      </span>
                      {item?.saleFlag && (
                        <span className="ml-6 text-sm leading-[22px] text-bravve-100 line-through">
                          ${item?.originPrice || item?.payPrice}
                        </span>
                      )}
                    </div>
                  ) : (
                    false
                  )}
                </div>
                <div className={"flex items-center justify-between"}>
                  {shopCartType == "checkout" ? (
                    <div className="text-[14px] leading-[22px] text-bravve-100">
                      {item.itemCount || item?.itemQuantity} Item
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <BravveInputNumber
                        isDrawer={isDrawer}
                        disabled={itemDisable}
                        key={item.id}
                        width={isMobile || isDrawer ? "94px" : "120px"}
                        height={isMobile ? "32px" : isDrawer ? "32px" : "40px"}
                        value={Number(item.itemCount)}
                        onChange={(type, value) => {
                          // 利用后台 加减购物车数量
                          calculateItem(
                            item.id,
                            type === "plus" ? "add" : "dec",
                          );
                        }}
                      />
                      <div
                        className={`${isMobile || isDrawer ? "ml-[1.6rem]" : "ml-[2.4rem]"} flex cursor-pointer items-center`}
                        onClick={() => deleteItem(item.id, index)}
                      >
                        <iconpark-icon
                          name="delet"
                          color="#402C22"
                          size={isMobile || isDrawer ? "18" : "22"}
                        ></iconpark-icon>
                      </div>
                    </div>
                  )}
                  <div>
                    {isMobile || (isDrawer && itemDisable) ? (
                      <span className="mt-[4px] flex text-[14px] leading-[22px] text-[#C31010]">
                        {item.cartProductStatusStr}
                      </span>
                    ) : (
                      false
                    )}
                    {isDrawer && !isMobile && !itemDisable ? (
                      <div className="mt-4">
                        {item?.saleFlag && (
                          <span
                            className={`mr-6 text-sm leading-[22px] text-bravve-sub-200 line-through`}
                          >
                            ${item?.originPrice || item?.payPrice}
                          </span>
                        )}

                        <span
                          className={`${itemDisable ? "text-bravve-sub-200" : "text-bravve-100"} text-[16px] font-medium leading-[24px]`}
                        >
                          ${item?.price || item?.payPrice}
                        </span>
                      </div>
                    ) : (
                      <></>
                    )}
                  </div>
                </div>
              </div>
              {(shopCartType == "shoppingCart" || shopCartType == "checkout") &&
                !isMobile && (
                  <div className="h-200 w-160">
                    {itemDisable ? (
                      false
                    ) : (
                      <>
                        <span
                          className={`flex justify-end text-[22px] font-medium leading-[30px] text-bravve-100`}
                        >
                          ${item?.price || item?.payPrice}
                        </span>
                        {
                          item?.saleFlag && <span className="flex justify-end text-[16px] leading-[24px] text-bravve-sub-100">
                          <span className="line-through">
                            ${item?.originPrice || item?.payPrice}
                          </span>
                        </span>
                        }
                      </>
                    )}
                    {itemDisable ? (
                      <span className="flex justify-end text-[16px] leading-[24px] text-bravve-sub-200">
                        {item.cartProductStatusStr}
                      </span>
                    ) : (
                      false
                    )}
                  </div>
                )}
            </div>
            {index == cartItems.length - 1 ? false : <Divider />}
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      {shopCartType === "shoppingCart" ? (
        <div className="text-[24px] font-bold leading-[32px] text-bravve-100">
          My Cart（{cartItems.length || 0}）
        </div>
      ) : (
        false
      )}
      {shopCartType === "checkout" ? (
        <div className={`mb-[2.4rem] mt-[6rem]`}>
          <span
            className={`cursor-pointer text-[20px] font-bold leading-[28px] text-bravve-100`}
            onClick={onSelectAll}
          >
            Order Goods
          </span>
        </div>
      ) : (
        <div className={`${isDrawer ? "mb-[24px]" : "mt-24"}`}>
          <Checkbox
            className="w-20"
            indeterminate={indeterminate}
            checked={selectedItems.length === cartItems.length}
            onChange={onSelectAll}
          />
          <span
            className={`ml-18 cursor-pointer text-[18px] font-bold leading-[26px] text-bravve-100`}
            onClick={onSelectAll}
          >
            Select All
          </span>
        </div>
      )}
      {shopCartType === "shoppingCart" ? <Divider /> : false}
      <Skeleton active loading={cartData.length === 0}>
        <List
          dataSource={cartItems}
          renderItem={(item, index) => <div>{renderItem(item, index)}</div>}
        />
      </Skeleton>
    </>
  );
};
